import React from 'react';
import styled from 'styled-components';
import { RE_EXPORT_STATUSES, reExportScoreBackfill } from '@services/scoreBackfills';
import { Body7, Container } from '@ui';
import { SkeletonButton, CopyButton } from '@ui/buttons';

// No box around the prefix — it sits flush with the SummaryBox's other lines so the whole
// summary reads as one column instead of a card inside a card.
const PrefixText = styled.code`
  display: block;
  padding: 0;
  color: ${({ theme }) => theme.themeColors.text};
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 12px;
  word-break: break-all;
  user-select: all;
`;

// Tone drives the color through the theme rather than a JS theme read, so this stays a plain
// presentational component with no hook or context dependency.
const NoticeText = styled.div`
  ${({ theme, $tone }) => ($tone === 'neutral' ? '' : `color: ${theme.themeColors[$tone]};`)}

  .base-text {
    ${({ theme, $tone }) => ($tone === 'neutral' ? '' : `color: ${theme.themeColors[$tone]};`)}
  }
`;

// The S3 snapshot a completed run wrote, plus the action that re-publishes it. Re-export reads
// the run's current factor_scores and writes a NEW uniquely-stamped folder every time — it never
// overwrites the original or a prior re-export — so this is safe to repeat and is the recovery
// path after scores are corrected, after an announce failure, or to regenerate under the current
// export format. No re-scoring, no model spend.
export const SnapshotOutput = ({ jobId, s3Prefix }) => {
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState(null);
  // Server value by default; a successful re-export moves the run's prefix, and the completed
  // job on this page won't refetch to learn that.
  const [reExportedPrefix, setReExportedPrefix] = React.useState(null);
  const prefix = reExportedPrefix || s3Prefix;

  const handleReExport = async () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    const { status, result, error } = await reExportScoreBackfill(jobId);
    setBusy(false);

    if (result?.status === RE_EXPORT_STATUSES.RE_EXPORTED) {
      if (result.s3_prefix) setReExportedPrefix(result.s3_prefix);
      setNotice({ tone: 'positive', message: 'Re-published. Downstream has been notified to re-download.' });
      return;
    }
    // Matched explicitly rather than as a catch-all for 200: an unreadable body already comes
    // back from the service as an error, and must not be reported as "nothing to export".
    if (result?.status === RE_EXPORT_STATUSES.NOTHING_TO_EXPORT) {
      setNotice({
        tone: 'neutral',
        message: result?.message || 'Nothing to export — this run has no completed scores to snapshot.',
      });
      return;
    }
    setNotice({
      tone: 'error',
      message: error,
      // 422 is caller-fixable — retrying the same run changes nothing. Everything else is transient.
      retryable: status !== 422,
      repairable: result?.repairable === true,
    });
  };

  // Nothing has been published yet when there's no prefix, so don't offer to re-publish one.
  const actionLabel = prefix ? 'Re-publish snapshot' : 'Publish snapshot';

  return (
    <Container top={20}>
      <Body7 bold>Output</Body7>
      {prefix ? (
        <>
          <Container top={6}>
            <PrefixText>{prefix}</PrefixText>
          </Container>
          <Container top={6}>
            <CopyButton text={prefix} transparent />
          </Container>
        </>
      ) : (
        <Container top={6}>
          <Body7 isLowContrast>No snapshot is recorded for this run.</Body7>
        </Container>
      )}

      <Container top={20}>
        <SkeletonButton onClick={handleReExport} disabled={busy} buttonProps={{ 'aria-disabled': busy || undefined }}>
          {busy ? 'Publishing…' : actionLabel}
        </SkeletonButton>
        <Container top={4}>
          <Body7 isLowContrast>
            Writes a new timestamped copy to S3 and re-notifies downstream. Doesn’t re-score, and never overwrites the
            existing folder.
          </Body7>
        </Container>
      </Container>

      {notice && (
        <Container top={8}>
          {/* One region for the whole notice: the follow-up sentences are the difference between
              "try again" and "panic", and were previously siblings that a reader could miss. */}
          <NoticeText $tone={notice.tone} role={notice.tone === 'error' ? 'alert' : 'status'}>
            <Body7>{notice.message}</Body7>
            {notice.repairable && (
              <Container top={2}>
                <Body7>The files uploaded — only the downstream notify failed. Retrying is safe.</Body7>
              </Container>
            )}
            {notice.retryable && !notice.repairable && (
              <Container top={2}>
                <Body7>Retrying is safe — nothing was overwritten.</Body7>
              </Container>
            )}
          </NoticeText>
        </Container>
      )}
    </Container>
  );
};
